// client/src/redux/Slices/EntitlementSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import entitlementApiInstance from "../../api/EntitlementApi";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { translateApiError } from "../../i18n/errorCodes";
import type { StreamEntitlement } from "../../types/billing";
import { finishCommandsForOperation } from "../../helpers/pendingCommand";
import {
  beginOperationRetry,
  completeOperationRetry,
  finishOperationRetry,
} from "./OperationsSlice";

interface EntitlementState {
  byStreamId: Record<string, StreamEntitlement>;
  requestId: Record<string, string | undefined>;
  operationRetryRequestId: Record<string, Record<string, string | undefined>>;
  loading: Record<string, boolean>;
  /** Last `plan_changed.pendingEncodes` per stream (converting banner). */
  pendingEncodes: Record<string, number>;
  /** Set on a failed fetch, cleared on success — drives ApiErrorState on the stream page. */
  error: Record<string, string | undefined>;
}

const initialState: EntitlementState = {
  byStreamId: {},
  requestId: {},
  operationRetryRequestId: {},
  loading: {},
  pendingEncodes: {},
  error: {},
};

export const entitlementSlice = createSlice({
  name: "entitlement",
  initialState,
  reducers: {
    beginEntitlementRequest(
      state,
      action: PayloadAction<{ streamId: string; requestId: string }>
    ) {
      state.requestId[action.payload.streamId] = action.payload.requestId;
    },
    setEntitlement(state, action: PayloadAction<StreamEntitlement>) {
      const previous = state.byStreamId[action.payload.liveStreamId];
      if (
        previous?.conversion &&
        action.payload.conversion &&
        previous.subscriptionId === action.payload.subscriptionId &&
        previous.plan === action.payload.plan &&
        previous.pendingPlan?.plan === action.payload.pendingPlan?.plan &&
        previous.conversion.revision > action.payload.conversion.revision
      )
        return;
      state.byStreamId[action.payload.liveStreamId] = action.payload;
    },
    setEntitlementLoading(
      state,
      action: PayloadAction<{ streamId: string; loading: boolean }>
    ) {
      state.loading[action.payload.streamId] = action.payload.loading;
    },
    setEntitlementError(
      state,
      action: PayloadAction<{ streamId: string; error: string | undefined }>
    ) {
      state.error[action.payload.streamId] = action.payload.error;
    },
    setPendingEncodes(
      state,
      action: PayloadAction<{ streamId: string; count: number }>
    ) {
      state.pendingEncodes[action.payload.streamId] = action.payload.count;
    },
    clearEntitlement(state, action: PayloadAction<string>) {
      delete state.requestId[action.payload];
      delete state.operationRetryRequestId[action.payload];
      delete state.byStreamId[action.payload];
      delete state.loading[action.payload];
      delete state.pendingEncodes[action.payload];
      delete state.error[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(beginOperationRetry, (state, action) => {
        delete state.requestId[action.payload.streamId];
        state.loading[action.payload.streamId] = false;
        state.operationRetryRequestId[action.payload.streamId] ??= {};
        state.operationRetryRequestId[action.payload.streamId][
          action.payload.operationId
        ] = action.payload.requestId;
      })
      .addCase(completeOperationRetry, (state, action) => {
        const { streamId, operationId, operation, requestId } = action.payload;
        if (
          state.operationRetryRequestId[streamId]?.[operationId] !== requestId
        )
          return;
        const entitlement = state.byStreamId[streamId];
        if (
          entitlement?.operation?.id === operationId &&
          operation.id === operationId &&
          (!operation.liveStreamId || operation.liveStreamId === streamId)
        )
          entitlement.operation = operation;
      })
      .addCase(finishOperationRetry, (state, action) => {
        const { streamId, operationId, requestId } = action.payload;
        if (
          state.operationRetryRequestId[streamId]?.[operationId] !== requestId
        )
          return;
        delete state.operationRetryRequestId[streamId][operationId];
        delete state.requestId[streamId];
        state.loading[streamId] = false;
      });
  },
});

export const {
  beginEntitlementRequest,
  setEntitlement,
  setEntitlementLoading,
  setEntitlementError,
  setPendingEncodes,
  clearEntitlement,
} = entitlementSlice.actions;

export const getEntitlement = createAsyncThunk(
  "entitlement/getEntitlement",
  async (
    { streamId, silent }: { streamId: string; silent: boolean },
    { dispatch, getState, requestId }
  ) => {
    dispatch(beginEntitlementRequest({ streamId, requestId }));
    const current = () =>
      (getState() as { entitlement: EntitlementState }).entitlement.requestId[
        streamId
      ] === requestId;
    dispatch(setEntitlementLoading({ streamId, loading: true }));
    try {
      const { data } = await entitlementApiInstance.getEntitlement(streamId);
      if (!current() || data.liveStreamId !== streamId) return data;
      dispatch(setEntitlement(data));
      if (
        data.operation &&
        ["completed", "cancelled", "superseded"].includes(data.operation.state)
      )
        finishCommandsForOperation(data.operation.id, streamId);
      dispatch(
        setPendingEncodes({
          streamId,
          count:
            (getState() as { entitlement: EntitlementState }).entitlement
              .byStreamId[streamId]?.conversion?.pending ?? 0,
        })
      );
      dispatch(setEntitlementError({ streamId, error: undefined }));
      return data;
    } catch (err: any) {
      if (!current()) throw err;
      dispatch(
        setEntitlementError({ streamId, error: translateApiError(err) })
      );
      if (!silent) dispatchErrorNotification(err);
      throw err;
    } finally {
      if (current())
        dispatch(setEntitlementLoading({ streamId, loading: false }));
    }
  }
);


export { getStreamOperations, retryStreamOperation } from "./OperationsSlice";
